import React, { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import * as d3 from "d3";

import { dataLayerConfig, playingAreaConfig } from "./utils.js";

import { Columns } from "@namroodinc/ni-ui";

import Axes from "./Axes.js";
import Defs from "./Defs.js";
import ExpectedThreat from "./ExpectedThreat.js";
import Grid from "./Grid.js";
import HeatMap from "./HeatMap.js";
import Pitch from "./Pitch.js";
import SavePlayingArea from "./SavePlayingArea.js";
import ScatterPlot from "./ScatterPlot.js";

function PlayingArea(props) {
  const {
    allowZoom,
    className,
    dataLayer,
    fullPitchView,
    isLandscape,
    lines,
    links,
    margin,
    markers,
    onClick,
    transparentMarkers,
    showAxes,
    showGrid,
    source,
    sport,
    strokeWidth,
    svgPropsOverride,
    title,
    xDomain,
    yDomain,
    zoomProps
  } = props;

  const ref = useRef(null);
  const zoomRef = useRef(null);
  const titleRef = useRef(null);
  const sourceRef = useRef(null);

  const text = useMemo(() => {
    let titleHeight = 0;

    if (title && titleRef.current) {
      const titleBBox = titleRef.current.getBBox();
      titleHeight = titleBBox.height;
    }

    let sourceHeight = 0;

    if (source && sourceRef.current) {
      const sourceBBox = sourceRef.current.getBBox();
      sourceHeight = sourceBBox.height;
    }

    return {
      titleHeight,
      sourceHeight
    };
  }, [source, title]);

  const fillColor = props.fillColor || playingAreaConfig[sport].fillColor;
  const strokeColor = props.strokeColor || playingAreaConfig[sport].strokeColor;

  const playingArea = useMemo(() => {
    const { height, width } = playingAreaConfig[sport];
    return { width, height };
  }, [sport]);

  let height = playingArea.height;
  let width = playingArea.width;

  if (!props.isLandscape) {
    [height, width] = [width, height];

    if (!props.fullPitchView) {
      height = playingArea.width / 2;
    }
  } else {
    if (!props.fullPitchView) {
      width = playingArea.height / 2;
    }
  }

  height =
    height + margin.top + margin.bottom + text.titleHeight + text.sourceHeight;
  width = width + margin.left + margin.right;

  let xRange = [0, isLandscape ? playingArea.width : playingArea.height];
  let yRange = [isLandscape ? playingArea.height : playingArea.width, 0];

  let x = d3
    .scaleLinear()
    .domain(isLandscape ? xDomain : yDomain)
    .range(xRange);
  let y = d3
    .scaleLinear()
    .domain(isLandscape ? yDomain : xDomain?.slice().reverse())
    .range(yRange);

  const { gridColumns, gridRows } = useMemo(() => {
    if (dataLayerConfig[dataLayer?.dataLayerType]?.adjustGridToMatchData) {
      return {
        gridColumns: dataLayer.data[0].length,
        gridRows: dataLayer.data.length
      };
    } else {
      return {
        gridColumns: props.gridColumns || 0,
        gridRows: props.gridRows || 0
      };
    }
  }, [dataLayer, props.gridColumns, props.gridRows]);

  const zoom = d3.zoom().on("zoom", function (event) {
    const { transform } = event;
    // const { x, y, k } = transform;
    // console.log(x, y, k);
    d3.select(zoomRef.current).attr("transform", transform);
  });

  useEffect(() => {
    if (!allowZoom) {
      d3.select(ref.current).on(".zoom", null);
    } else {
      if (allowZoom) {
        d3.select(ref.current).call(zoom);
      }
    }
  }, [allowZoom, zoom]);

  useEffect(() => {
    if (zoomProps?.x && zoomProps?.y && zoomProps?.k) {
      d3.select(ref.current).call(
        zoom.transform,
        d3.zoomIdentity.translate(zoomProps.x, zoomProps.y).scale(zoomProps.k)
      );
    }
  }, [zoom.transform, zoomProps]);

  return (
    <Columns numberOfColumns={1}>
      {props.showDownloadButtons && <SavePlayingArea playingAreaRef={ref} />}
      <svg
        className={className || ""}
        height={height}
        width={width}
        viewBox={`0 0 ${width} ${height}`}
        onClick={
          onClick
            ? (event) => {
                const [xPos, yPos] = d3.pointer(event);
                onClick({
                  x: x.invert(xPos - (isLandscape ? margin.top : margin.left)),
                  y: y.invert(yPos - (isLandscape ? margin.left : margin.top))
                });
              }
            : undefined
        }
        ref={ref}
        {...svgPropsOverride}
      >
        <Defs
          fullPitchView={fullPitchView}
          height={playingArea.height}
          isLandscape={isLandscape}
          width={playingArea.width}
        />
        <g
          ref={zoomRef}
          fontFamily={`"Helvetica Neue", Helvetica, HB TC, HB JP, HB KR, Arial,
          "Segoe UI", “microsoft jhenghei”, “microsoft mhei”, -apple-system,
          BlinkMacSystemFont, sans-serif`}
        >
          <g>
            <rect x={0} y={0} width={width} height={height} fill={fillColor} />
          </g>
          <g
            transform={`translate(${margin.left}, ${
              margin.top + text.titleHeight
            })`}
          >
            {dataLayer.dataLayerType === "heatMap" && (
              <HeatMap
                data={dataLayer.data}
                height={playingArea.height}
                isLandscape={isLandscape}
                sport={sport}
                x={x}
                y={y}
                width={playingArea.width}
              />
            )}
            <g
              transform={`rotate(${isLandscape ? 0 : 90}) translate(${0}, ${
                isLandscape ? 0 : -(width - margin.left - margin.right)
              })`}
            >
              <Pitch
                {...props}
                fillColor={fillColor}
                strokeColor={strokeColor}
              />
              {showGrid && (
                <Grid
                  gridColumns={gridColumns}
                  gridRows={gridRows}
                  height={playingArea.height}
                  strokeColor={strokeColor}
                  strokeWidth={strokeWidth}
                  width={playingArea.width}
                />
              )}
              {dataLayer.dataLayerType === "xT" && (
                <ExpectedThreat
                  data={dataLayer.data}
                  gridColumns={gridColumns}
                  gridRows={gridRows}
                  height={playingArea.height}
                  isLandscape={isLandscape}
                  width={playingArea.width}
                />
              )}
            </g>
            {showAxes && (
              <Axes
                height={playingArea.height}
                isLandscape={isLandscape}
                margin={margin}
                x={x}
                xRange={xRange}
                y={y}
                yRange={yRange}
                width={playingArea.width}
              />
            )}
            <ScatterPlot
              fillColor={fillColor}
              height={playingArea.height}
              isLandscape={isLandscape}
              lines={lines}
              links={links}
              margin={margin}
              markers={markers}
              transparentMarkers={transparentMarkers}
              x={x}
              xRange={xRange}
              y={y}
              yRange={yRange}
              width={playingArea.width}
            />
          </g>
          <g
            transform={`translate(${margin.left}, ${
              (margin.top + text.titleHeight) / 2
            })`}
          >
            {title && (
              <text
                alignmentBaseline="middle"
                fill={strokeColor}
                fontSize={20}
                fontWeight={700}
                ref={titleRef}
                x={0}
                y={0}
              >
                {title}
              </text>
            )}
            {source && (
              <text
                alignmentBaseline="baseline"
                fill={strokeColor}
                fontSize={12}
                fontWeight={400}
                ref={sourceRef}
                x={0}
                y={height - text.sourceHeight - text.titleHeight}
              >
                {source}
              </text>
            )}
          </g>
        </g>
      </svg>
    </Columns>
  );
}

PlayingArea.defaultProps = {
  allowZoom: false,
  brandColor: null,
  className: null,
  dataLayer: {},
  fillColor: null,
  footnote: null,
  gridColumns: 16,
  gridRows: 12,
  fullPitchView: true,
  isLandscape: true,
  lines: [],
  links: [],
  margin: {
    top: 30,
    right: 50,
    bottom: 30,
    left: 30
  },
  markers: [],
  onClick: null,
  transparentMarkers: false,
  showAxes: false,
  showDownloadButtons: false,
  showGrid: false,
  source: null,
  sport: "soccer",
  strokeColor: null,
  strokeWidth: 2,
  subTitle: null,
  svgPropsOverride: {},
  title: null,
  xDomain: [0, 100],
  yDomain: [0, 100],
  zoomProps: {
    x: null,
    y: null,
    k: null
  }
};

PlayingArea.propTypes = {
  allowZoom: PropTypes.bool,
  brandColor: PropTypes.string,
  className: PropTypes.string,
  dataLayer: PropTypes.shape({
    data: PropTypes.array,
    dataLayerType: PropTypes.oneOf(["heatMap", "xT"])
  }),
  fillColor: PropTypes.string,
  footnote: PropTypes.string,
  gridColumns: PropTypes.number,
  gridRows: PropTypes.number,
  fullPitchView: PropTypes.bool,
  isLandscape: PropTypes.bool,
  lines: PropTypes.array,
  links: PropTypes.array,
  markers: PropTypes.array,
  margin: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number
  }),
  onClick: PropTypes.func,
  transparentMarkers: PropTypes.bool,
  showAxes: PropTypes.bool,
  showDownloadButtons: PropTypes.bool,
  showGrid: PropTypes.bool,
  source: PropTypes.string,
  sport: PropTypes.oneOf(["americanFootball", "basketball", "soccer"]),
  strokeColor: PropTypes.string,
  strokeWidth: PropTypes.number,
  subTitle: PropTypes.string,
  svgPropsOverride: PropTypes.object,
  title: PropTypes.string,
  xDomain: PropTypes.array,
  yDomain: PropTypes.array,
  zoomProps: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
    k: PropTypes.number
  })
};

export default PlayingArea;
